import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PAYMENT_TYPE from 'extraService/datasets/payment_type';
import FormContainer from 'core/components/forms/FormContainer';
import SubmitButton from 'core/components/buttons/SubmitButton';
import { ExtraServiceOrderFormProps } from './index';
import { EXTRA_SERVICE_ORDER_ACTION } from 'extraService/datasets/extra_service_order_action';
import { ExtraServiceOrderFormData } from 'extraService/types/extraServiceOrder';
import DATE_PICKER_TYPE from 'core/datasets/date_picker_type';
import ExtraServiceRequestConfirmationModal from 'pages/ExtraService/components/ExtraServiceRequestConfirmationModal/ExtraServiceRequestConfirmationModal';
import { extraServiceAmountIsValid } from '../RepeatingServiceOrderFields/helpers';
import {
    datesAreValidAndSameAndHasAmount,
    generatedExtraServiceFormClasses,
    generateMaxDate,
    generateMinDate,
} from 'extraService/utils/useExtraServiceHelper';
import { DateTime } from 'luxon';
import FormDatePicker from '../../../../core/components/selectors/FormDatePicker/FormDatePicker';
import OrderServiceAmountTextField from '../RepeatingServiceOrderFields/OrderServiceAmountTextField';
import { isNull } from 'lodash';

const ExtraServiceOrderForm = ({
    action = EXTRA_SERVICE_ORDER_ACTION.ORDER,
    paymentType,
    serviceName,
    price,
    dateFrom,
    dateUntil,
    orderId,
    isConfirmed,
    initialFormData,
    onSubmit,
    onClose,
    loading = false,
}: ExtraServiceOrderFormProps): JSX.Element => {
    const { t } = useTranslation();
    const classes = generatedExtraServiceFormClasses;

    const [isFormValueSame, setIsFormValueSame] = useState(false);
    const [modalClosed, setModalClosed] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState<ExtraServiceOrderFormData>(initialFormData);

    const isNotCancelAction = action !== EXTRA_SERVICE_ORDER_ACTION.CANCEL;
    const isOrderAction = action === EXTRA_SERVICE_ORDER_ACTION.ORDER;
    const isNotCancelActionOrConfirmed = isNotCancelAction || isConfirmed;

    useEffect(() => {
        if (!isOrderAction && dateFrom) {
            setFormData((current) => ({ ...current, fromDate: DateTime.fromSQL(dateFrom) }));
        }

        if (!isOrderAction && dateUntil) {
            setFormData((current) => ({ ...current, untilDate: DateTime.fromSQL(dateUntil) }));
        }
    }, [isOrderAction, dateFrom, dateUntil]);

    useEffect(() => {
        setIsFormValueSame(datesAreValidAndSameAndHasAmount(formData, dateFrom, dateUntil));
    }, [dateFrom, dateUntil, formData]);

    const isSubmitButtonDisabled =
        !extraServiceAmountIsValid(formData?.amount || 0) || (!isOrderAction && isFormValueSame);

    const isRepeatingPayment = paymentType === PAYMENT_TYPE.REPEATING;
    const isChangeAction = action === EXTRA_SERVICE_ORDER_ACTION.CHANGE;
    const isCancelAction = action === EXTRA_SERVICE_ORDER_ACTION.CANCEL;

    const canEditFromDate =
        !isCancelAction && (isOrderAction || !isConfirmed || (!isRepeatingPayment && isChangeAction));
    const canEditUntilDate = !isCancelAction && isRepeatingPayment && (isOrderAction || !isConfirmed || isChangeAction);

    const submitHandler = async () => {
        setIsSubmitting(true);
        if (isNull(orderId)) {
            setIsSubmitting(false);
            return;
        }

        try {
            await onSubmit(formData);
        } finally {
            setIsSubmitting(false);
        }

        onClose();
    };

    const dateInputPaymentClass = `extra-service-order-form__date-input--${
        paymentType === PAYMENT_TYPE.REPEATING ? 'repeating' : 'single'
    }`;

    return (
        <FormContainer>
            <form className={classes.base} role={'form'}>
                {isRepeatingPayment
                    ? isNotCancelActionOrConfirmed && (
                          <>
                              <div className={classes.periodContainer}>
                                  <FormDatePicker
                                      isDisabled={!canEditFromDate}
                                      isRequired={true}
                                      clearable={false}
                                      minDate={generateMinDate({
                                          dateType: DATE_PICKER_TYPE.DATE_FROM,
                                          paymentType,
                                          formData,
                                      })}
                                      maxDate={generateMaxDate({
                                          dateType: DATE_PICKER_TYPE.DATE_FROM,
                                          paymentType,
                                          formData,
                                      })}
                                      classes={`extra-service-order-form__date-from-input extra-service-order-form__input ${dateInputPaymentClass}`}
                                      label={t(`extraServices.order.form.${DATE_PICKER_TYPE.DATE_FROM}.${paymentType}`)}
                                      value={
                                          formData[
                                              DATE_PICKER_TYPE.DATE_FROM as keyof ExtraServiceOrderFormData
                                          ] as DateTime | null
                                      }
                                      onChange={(next: DateTime | null) =>
                                          setFormData((current) => {
                                              return { ...current, fromDate: next };
                                          })
                                      }
                                  />
                                  <FormDatePicker
                                      isRequired={false}
                                      isDisabled={!canEditUntilDate}
                                      clearable={true}
                                      minDate={generateMinDate({
                                          dateType: DATE_PICKER_TYPE.DATE_UNTIL,
                                          paymentType,
                                          formData,
                                      })}
                                      maxDate={generateMaxDate({
                                          dateType: DATE_PICKER_TYPE.DATE_UNTIL,
                                          paymentType,
                                          formData,
                                      })}
                                      classes={`extra-service-order-form__date-until-input extra-service-order-form__input ${dateInputPaymentClass}`}
                                      label={t(
                                          `extraServices.order.form.${DATE_PICKER_TYPE.DATE_UNTIL}.${paymentType}`,
                                      )}
                                      value={
                                          formData[
                                              DATE_PICKER_TYPE.DATE_UNTIL as keyof ExtraServiceOrderFormData
                                          ] as DateTime | null
                                      }
                                      onChange={(next: DateTime | null) =>
                                          setFormData((current) => {
                                              return { ...current, untilDate: next };
                                          })
                                      }
                                  />
                              </div>
                              {isOrderAction && (
                                  <OrderServiceAmountTextField
                                      className={`${generatedExtraServiceFormClasses.inputContainer} extra-service-order-form__amount-input`}
                                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                          setFormData((current) => {
                                              const next = Number(event.target.value);
                                              return { ...current, amount: isNaN(next) ? current.amount : next };
                                          });
                                      }}
                                      label={t('extraServices.order.form.amount')}
                                      value={formData.amount}
                                  />
                              )}
                          </>
                      )
                    : isNotCancelAction && (
                          <div className={classes.periodContainer}>
                              <FormDatePicker
                                  isDisabled={!canEditFromDate}
                                  isRequired={true}
                                  clearable={false}
                                  minDate={generateMinDate({
                                      dateType: DATE_PICKER_TYPE.DATE_FROM,
                                      paymentType,
                                      formData,
                                  })}
                                  maxDate={generateMaxDate({
                                      dateType: DATE_PICKER_TYPE.DATE_FROM,
                                      paymentType,
                                      formData,
                                  })}
                                  classes={`extra-service-order-form__date-from-input extra-service-order-form__input ${dateInputPaymentClass}`}
                                  label={t(`extraServices.order.form.${DATE_PICKER_TYPE.DATE_FROM}.${paymentType}`)}
                                  value={
                                      formData[
                                          DATE_PICKER_TYPE.DATE_FROM as keyof ExtraServiceOrderFormData
                                      ] as DateTime | null
                                  }
                                  onChange={(next: DateTime | null) =>
                                      setFormData((current) => {
                                          return { ...current, fromDate: next };
                                      })
                                  }
                              />
                          </div>
                      )}
                <div className={classes.submitButtonContainer}>
                    <SubmitButton
                        disabled={isSubmitButtonDisabled}
                        text={t('extraServices.order.form.submitButtonLabel')}
                        actionHandler={() => setModalClosed(false)}
                    />
                </div>
                <ExtraServiceRequestConfirmationModal
                    action={action}
                    submitHandler={submitHandler}
                    paymentType={paymentType}
                    serviceName={serviceName}
                    formData={formData}
                    price={price}
                    modalArgs={{
                        closed: modalClosed,
                        handleClosed: () => setModalClosed(true),
                        size: 'lg',
                        showTitle: false,
                        confirmLabel: t('extraServices.order.form.confirmButtonOk'),
                        cancelLabel: t('extraServices.order.form.confirmButtonDontSend'),
                    }}
                    loading={loading || isSubmitting}
                />
            </form>
        </FormContainer>
    );
};

export default ExtraServiceOrderForm;
